import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ProductInterface } from "../../common/interfaces/ProductInterface";
import { loadProductImages } from "../../common/utils/imageLoader";
import { getLocalizedText } from "../../common/utils/getLocalizedText";

const filterProducts = (
    products: ProductInterface[],
    searchValue: string,
): ProductInterface[] => {
    if (!searchValue || searchValue.trim() === "") return [];

    const lowerSearchValue = searchValue.toLowerCase();

    return products.filter((product: ProductInterface) => {
        const label = getLocalizedText(product.label);
        const matchesName = label.toLowerCase().includes(lowerSearchValue);
        const matchesArticleNumber = product.identifier
            ?.toLowerCase()
            .includes(lowerSearchValue);

        return matchesName || matchesArticleNumber;
    });
};

const categorizeProducts = (
    filteredProducts: ProductInterface[],
): {
    machinery: ProductInterface[];
    accessories: ProductInterface[];
    archived: ProductInterface[];
} => {
    const machinery: ProductInterface[] = [];
    const accessories: ProductInterface[] = [];
    const archived: ProductInterface[] = [];

    filteredProducts.forEach((product: ProductInterface) => {
        if (product.productFamily?.code === "old_models") {
            archived.push(product);
            return;
        }

        if (product.productFamily?.code === "accessories") {
            accessories.push(product);
            return;
        }

        machinery.push(product);
    });

    return { machinery, accessories, archived };
};

export const performSearch = createAsyncThunk(
    "search/performSearch",
    async (payload: {
        products: ProductInterface[];
        searchValue: string;
        getLocalImageUrl: (cdnUrl: string) => Promise<string | null>;
    }) => {
        const { products, searchValue, getLocalImageUrl } = payload;

        const productImages = await loadProductImages(
            products,
            getLocalImageUrl,
        );

        const filteredProducts = filterProducts(products, searchValue);
        const categorizedResults = categorizeProducts(filteredProducts);

        return {
            ...categorizedResults,
            productImages,
            resultInputValue: searchValue,
            resultCount: filteredProducts.length,
        };
    },
);

interface SearchPageInterface {
    machinery: ProductInterface[];
    accessories: ProductInterface[];
    archived: ProductInterface[];
    resultInputValue: string;
    resultCount: number;
    isLoading: boolean;
    productImages: { [key: string]: string };
}

const initialState: SearchPageInterface = {
    machinery: [],
    accessories: [],
    archived: [],
    resultInputValue: "",
    resultCount: 0,
    isLoading: false,
    productImages: {},
};

const searchSlice = createSlice({
    name: "search",
    initialState,
    reducers: {
        clearResults: (state) => {
            state.machinery = [];
            state.accessories = [];
            state.archived = [];
            state.resultCount = 0;
            state.resultInputValue = "";
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(performSearch.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(performSearch.fulfilled, (state, action) => {
                state.machinery = action.payload.machinery;
                state.accessories = action.payload.accessories;
                state.archived = action.payload.archived;
                state.productImages = action.payload.productImages;
                state.resultInputValue = action.payload.resultInputValue;
                state.resultCount = action.payload.resultCount;
                state.isLoading = false;
            })
            .addCase(performSearch.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const searchReducer = searchSlice.reducer;

export const { clearResults } = searchSlice.actions;
